import React, { useMemo } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import OpenHouseRow from "./OpenHouseRow";
import { todayIso } from "./utils";

export default function DaySection({ mode, groupKey, items, selectedIds, onToggle, onToggleVisited, onOpenDetail, onSelectAll }) {
  let headerLeft;
  let isToday = false;
  if (mode === "agenda") {
    const dt = new Date(groupKey + "T12:00:00");
    const weekday = dt.toLocaleDateString(undefined, { weekday: "long" });
    const monthDay = dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    isToday = groupKey === todayIso();
    headerLeft = (
      <>
        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">{weekday}</span>
        <span className="text-lg font-semibold text-slate-900 font-display tabular-nums">{monthDay}</span>
        {isToday ? (
          <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded bg-amber-200 text-amber-900 font-medium">
            Today
          </span>
        ) : null}
      </>
    );
  } else {
    headerLeft = (
      <>
        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Neighborhood</span>
        <span className="text-lg font-semibold text-slate-900 font-display">{groupKey}</span>
      </>
    );
  }

  // Sub-group items by city when there's more than one distinct city
  const cityGroups = useMemo(() => {
    if (mode !== "agenda") return null;
    const cities = new Set(items.map((oh) => oh.city || ""));
    if (cities.size <= 1) return null; // single city — no sub-headers needed
    const groups = new Map();
    for (const oh of items) {
      const city = oh.city || "Other";
      if (!groups.has(city)) groups.set(city, []);
      groups.get(city).push(oh);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items, mode]);

  const allSelected = items.length > 0 && items.every((oh) => selectedIds.has(oh.id));
  return (
    <section className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`group-section-${groupKey}`}>
      <header
        className={`px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2 ${
          isToday ? "bg-amber-50" : "bg-slate-50"
        }`}
      >
        <div className="flex items-baseline gap-3">
          {headerLeft}
          <span className="text-xs text-slate-500">{items.length} listing{items.length === 1 ? "" : "s"}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onSelectAll}
          className="text-xs min-h-[40px]"
          data-testid={`select-all-group-${groupKey}`}
        >
          {allSelected ? "Deselect all" : "Select all"}
        </Button>
      </header>
      {cityGroups ? (
        /* Multi-city day — show city sub-headers */
        <div>
          {cityGroups.map(([city, cityItems]) => (
            <div key={city}>
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-50/70 border-b border-slate-100">
                <MapPin className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{city}</span>
                <span className="text-xs text-slate-400">({cityItems.length})</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {cityItems.map((oh) => (
                  <OpenHouseRow
                    key={oh.id}
                    oh={oh}
                    mode={mode}
                    selected={selectedIds.has(oh.id)}
                    onToggle={() => onToggle(oh.id)}
                    onToggleVisited={() => onToggleVisited(oh)}
                    onOpenDetail={() => onOpenDetail(oh.id)}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        /* Single city or neighborhood mode — flat list */
        <ul className="divide-y divide-slate-100">
          {items.map((oh) => (
            <OpenHouseRow
              key={oh.id}
              oh={oh}
              mode={mode}
              selected={selectedIds.has(oh.id)}
              onToggle={() => onToggle(oh.id)}
              onToggleVisited={() => onToggleVisited(oh)}
              onOpenDetail={() => onOpenDetail(oh.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
